"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Loader2, ImageOff } from "lucide-react";

const SIZE_CLASSES = "w-[180px] h-[180px] lg:w-[236px] lg:h-[236px]";

/**
 * Card image with loading spinner, fade-in transition, and error fallback.
 * Images stay visible in SSR/no-JS; spinner/fade-in only after hydration.
 */
export default function CardImage({ src, alt, priority = false }) {
  const imgRef = useRef(null);
  const [hasJs, setHasJs] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setHasJs(true);
    setError(false);

    // Priority/cached images often finish before hydration, so onLoad may not fire again.
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [src]);

  if (!src || error) {
    return (
      <div
        className={`${SIZE_CLASSES} flex items-center justify-center text-gray-400/80`}
        role="img"
        aria-label={alt}
      >
        <ImageOff className="w-10 h-10" aria-hidden />
      </div>
    );
  }

  return (
    <div className={`relative ${SIZE_CLASSES} flex items-center justify-center`}>
      {hasJs && !loaded && !priority && (
        <Loader2 className="absolute w-10 h-10 text-gray-400 animate-spin" aria-hidden />
      )}
      <Image
        key={src}
        ref={imgRef}
        src={src}
        alt={alt}
        width={236}
        height={236}
        priority={priority}
        fetchPriority={priority ? "high" : "auto"}
        quality={65}
        sizes="(max-width: 1024px) 180px, 236px"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={`${SIZE_CLASSES} object-contain transition-opacity duration-300 ease-out ${
          !hasJs || loaded || priority ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}
